/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 01/09/2025 - 21:49:01
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 01/09/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect } from "react";
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Button, 
  Container, 
  SwipeableDrawer, 
  IconButton, 
  Typography, 
  Grid, 
  useTheme, 
  useMediaQuery,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  alpha,
  styled,
  Menu,
  MenuItem,
  Tabs,
  Tab
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EastIcon from '@mui/icons-material/East';

// Import images (using your existing imports)
import Logo from "../assests/images/component/logo.png";
import CMS_LMS from "../assests/images/component/cms and lms.svg";
import HMS from "../assests/images/component/hms.svg";
import WebDevelopment from "../assests/images/component/web development.svg";
import MobileApp from "../assests/images/component/mobile app.svg";
import Seo from "../assests/images/component/seo.svg";
import Smm from "../assests/images/component/smm.svg";
import Feature_1 from "../assests/images/component/customizable.svg";
import Feature_2 from "../assests/images/component/management 1.svg";
import Feature_3 from "../assests/images/component/outsourcing 1.svg";
import Feature_4 from "../assests/images/component/shield 1.svg";

// Define service categories
const serviceCategories = [
  {
    title: "Software Development",
    items: [
      { title: "CMS and LMS", icon: CMS_LMS, text: "Simplify, control & grow", route: "services/cms-lms" },
      { title: "HMS", icon: HMS, text: "Fast, responsive and robust", route: "services/hms" },
      { title: "Web Development", icon: WebDevelopment, text: "All your digital needs", route: "services/web-development" },
      { title: "E-Commerce", icon: MobileApp, text: "Online store with all tools", route: "services/e-commerce" },
      { title: "Mobile App", icon: MobileApp, text: "Android, iOS and Hybrid", route: "services/mobile-development" },
      { title: "Custom Software", icon: MobileApp, text: "Tailored to your needs", route: "services/custom-software-development" }
    ]
  },
  {
    title: "Design & Management",
    items: [
      { title: "UI/UX Design", icon: Seo, text: "Premium web & mobile designs", route: "services/ui-ux-design" },
      { title: "Product Management", icon: Feature_2, text: "Expert product guidance", route: "productmanagement" },
      { title: "Research & Development", icon: Feature_3, text: "Innovative solutions", route: "research" }
    ]
  },
  {
    title: "Digital Evolution",
    items: [
      { title: "Digital Transformation", icon: Feature_1, text: "Modernize your business", route: "digitaltransformation" },
      { title: "Data Science & AI", icon: Feature_2, text: "Intelligent automation", route: "datascience" },
      { title: "Blockchain", icon: Feature_3, text: "Secure distributed systems", route: "blockchain" },
      { title: "AR/VR", icon: Feature_4, text: "Immersive experiences", route: "arvr" }
    ]
  },
  {
    title: "Support & Marketing",
    items: [
      { title: "Quality Assurance", icon: Feature_4, text: "Comprehensive testing", route: "qa" },
      { title: "SEO", icon: Seo, text: "On-Page & Off-Page", route: "seo" },
      { title: "Digital Marketing", icon: Smm, text: "Grow your online presence", route: "digitalmarketing" }
    ]
  }
];

// Styled components
const StyledAppBar = styled(AppBar)<{ transparent: boolean }>(({ theme, transparent }) => ({
  backgroundColor: transparent ? 'rgba(255, 255, 255, 0.8)' : '#ffffff',
  backdropFilter: transparent ? 'blur(10px)' : 'none',
  boxShadow: transparent ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.05)',
  position: 'fixed',
  top: 0,
  zIndex: 1000,
  transition: 'all 0.3s ease'
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: '#333',
  fontWeight: 500,
  fontSize: '15px',
  textTransform: 'none',
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  padding: '6px 12px',
  borderRadius: '4px',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    '&::after': {
      width: '80%',
      opacity: 1
    }
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '10%',
    width: '0%',
    height: '2px',
    backgroundColor: theme.palette.primary.main,
    transition: 'all 0.3s ease',
    opacity: 0
  }
}));

const ContactButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#00509d',
  color: 'white',
  fontWeight: 600,
  borderRadius: '30px',
  padding: '8px 24px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0, 80, 157, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#003c75',
    boxShadow: '0 6px 16px rgba(0, 80, 157, 0.3)',
    transform: 'translateY(-2px)'
  }
}));

const ServiceItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transform: 'translateX(3px)'
  }
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '8px',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
    marginTop: '10px',
    maxWidth: '650px'
  }
}));

const DrawerContainer = styled(Box)(({ theme }) => ({
  width: '300px',
  paddingTop: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}));

// Main Navbar component
const Navbar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [mobileMenuExpanded, setMobileMenuExpanded] = useState<string | null>(null);
  const [transparent, setTransparent] = useState(false);

  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      // Make navbar transparent when scrolling down
      if (window.scrollY > 50) {
        setTransparent(true);
      } else {
        setTransparent(false);
      }
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleServicesClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isMobile) {
      setServicesAnchorEl(servicesAnchorEl ? null : event.currentTarget);
      setActiveTab(0); // Reset to first tab when opening
    } else {
      navigate('/services');
      setDrawerOpen(false);
    }
  };

  const handleServicesClose = () => {
    setServicesAnchorEl(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleNavigate = (route: string) => {
    // Close any open menus
    setServicesAnchorEl(null);
    setDrawerOpen(false);
    
    // Format route and navigate
    const formattedRoute = route.toLowerCase().replace(/\s+/g, '');
    if (route === "Home") {
      navigate('/');
    } else {
      navigate(`/${formattedRoute}`);
    }
  };

  const toggleMobileCategory = (category: string) => {
    setMobileMenuExpanded(mobileMenuExpanded === category ? null : category);
  };

  // Service menu open state
  const open = Boolean(servicesAnchorEl);

  return (
    <>
      {/* Main Navbar */}
      <StyledAppBar transparent={transparent}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ py: 1 }}>
            {/* Logo */}
            <Box 
              sx={{ 
                flexGrow: 0, 
                display: 'flex', 
                mr: 2,
                cursor: 'pointer'
              }}
              onClick={() => handleNavigate('Home')}
            >
              <img src={Logo} alt="Smart IT Innovation" style={{ height: '50px' }} />
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
              <NavButton onClick={() => handleNavigate('Home')}>
                Home
              </NavButton>
              
              <NavButton 
                onClick={handleServicesClick}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              >
                Services
              </NavButton>
              
              <NavButton onClick={() => handleNavigate('About Us')}>
                About Us
              </NavButton>
              
              <NavButton onClick={() => handleNavigate('Portfolio')}>
                Portfolio
              </NavButton>
              
              <NavButton onClick={() => handleNavigate('Career')}>
                Career
              </NavButton>
              
              <NavButton onClick={() => handleNavigate('Technologies')}>
                Technologies
              </NavButton>
            </Box>

            {/* Contact Button */}
            <Box sx={{ flexGrow: 0 }}>
              <ContactButton 
                variant="contained" 
                onClick={() => handleNavigate('contacts')}
              >
                CONTACT
              </ContactButton>
            </Box>

            {/* Mobile Menu Button */}
            <Box sx={{ flexGrow: 0, display: { xs: 'flex', md: 'none' }, ml: 2 }}>
              <IconButton
                size="large"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={() => setDrawerOpen(true)}
                color="primary"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>

        {/* Compact Services Menu with Tabs */}
        <StyledMenu
          id="services-menu"
          anchorEl={servicesAnchorEl}
          open={open}
          onClose={handleServicesClose}
          MenuListProps={{
            'aria-labelledby': 'services-button',
            sx: { p: 0 }
          }}
        >
          <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{
                style: { backgroundColor: theme.palette.primary.main }
              }}
            >
              {serviceCategories.map((category, index) => (
                <Tab 
                  key={category.title} 
                  label={category.title} 
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 500,
                    '&.Mui-selected': {
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    }
                  }}
                />
              ))}
            </Tabs>
          </Box>
          
          {serviceCategories.map((category, index) => (
            <Box 
              key={category.title}
              role="tabpanel"
              hidden={activeTab !== index}
              id={`services-tabpanel-${index}`}
              aria-labelledby={`services-tab-${index}`}
              sx={{ p: 2 }}
            >
              {activeTab === index && (
                <Grid container spacing={1}>
                  {category.items.map((item) => (
                    <Grid item xs={12} sm={6} key={item.title}>
                      <ServiceItem onClick={() => handleNavigate(item.route)}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.5,
                            borderRadius: '6px',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1)
                          }}
                        >
                          <img src={item.icon} alt={item.title} width="20" height="20" />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" fontWeight={500}>
                            {item.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.text}
                          </Typography>
                        </Box>
                        <EastIcon 
                          sx={{ 
                            fontSize: 14, 
                            color: theme.palette.primary.main,
                            opacity: 0.7
                          }} 
                        />
                      </ServiceItem>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          ))}
        </StyledMenu>
      </StyledAppBar>

      {/* Spacer to compensate for fixed navbar */}
      <Toolbar />

      {/* Mobile Drawer */}
      <SwipeableDrawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
        PaperProps={{
          sx: {
            width: '300px',
            borderTopLeftRadius: '12px',
            borderBottomLeftRadius: '12px'
          }
        }}
      >
        <DrawerContainer>
          {/* Drawer Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src={Logo} alt="Smart IT Innovation" style={{ height: '40px' }} />
            </Box>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Divider />
          
          {/* Drawer Navigation */}
          <List sx={{ flexGrow: 1, pt: 2 }}>
            <ListItemButton onClick={() => handleNavigate('Home')}>
              <ListItemText primary="Home" />
            </ListItemButton>
            
            <ListItemButton onClick={() => toggleMobileCategory('Services')}>
              <ListItemText primary="Services" />
              {mobileMenuExpanded === 'Services' ? 
                <KeyboardArrowUpIcon /> : 
                <KeyboardArrowDownIcon />
              }
            </ListItemButton>
            
            <Collapse in={mobileMenuExpanded === 'Services'} timeout="auto" unmountOnExit>
              {serviceCategories.map((category) => (
                <Box key={category.title}>
                  <ListItemButton 
                    onClick={() => toggleMobileCategory(category.title)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText 
                      primary={category.title} 
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    {mobileMenuExpanded === category.title ? 
                      <KeyboardArrowUpIcon fontSize="small" /> : 
                      <KeyboardArrowDownIcon fontSize="small" />
                    }
                  </ListItemButton>
                  
                  <Collapse in={mobileMenuExpanded === category.title} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {category.items.map((item) => (
                        <ListItemButton 
                          key={item.title} 
                          sx={{ pl: 6 }}
                          onClick={() => handleNavigate(item.route)}
                        >
                          <ListItemText 
                            primary={item.title}
                            secondary={item.text}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              ))}
            </Collapse>
            
            <ListItemButton onClick={() => handleNavigate('About Us')}>
              <ListItemText primary="About Us" />
            </ListItemButton>
            
            <ListItemButton onClick={() => handleNavigate('Portfolio')}>
              <ListItemText primary="Portfolio" />
            </ListItemButton>
            
            <ListItemButton onClick={() => handleNavigate('Career')}>
              <ListItemText primary="Career" />
            </ListItemButton>
            
            <ListItemButton onClick={() => handleNavigate('Technologies')}>
              <ListItemText primary="Technologies" />
            </ListItemButton>
          </List>
          
          {/* Drawer Footer */}
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={() => handleNavigate('Contact Us')}
              sx={{
                borderRadius: '30px',
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Contact Us
            </Button>
          </Box>
        </DrawerContainer>
      </SwipeableDrawer>
    </>
  );
};

export default Navbar;